import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaSearch } from 'react-icons/fa';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path;

  const casinos = [
    { id: 'allbritishcasino', name: 'All British Casino', url: '/offers/allbritishcasino' },
    { id: 'funcasino', name: 'Fun Casino', url: '/offers/funcasino' },
    { id: 'kwiff', name: 'Kwiff', url: '/offers/kwiff' }
  ];

  const pages = [
    { name: 'Home', url: '/' },
    { name: 'Compare Offers', url: '/comparison' },
    { name: 'About Us', url: '/about' },
    { name: 'FAQ', url: '/faq' }
  ];

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results: any[] = [];

      // Search casinos
      casinos.forEach(casino => {
        if (casino.name.toLowerCase().includes(query)) {
          results.push({ ...casino, type: 'casino' });
        }
      });

      // Search pages
      pages.forEach(page => {
        if (page.name.toLowerCase().includes(query)) {
          results.push({ ...page, type: 'page' });
        }
      });

      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
    };

    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      inputRef.current?.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen]);

  const handleSearchClick = () => {
    setIsSearchOpen(true);
  };

  const handleResultClick = (url: string) => {
    navigate(url);
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-30">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/images/logo.webp" 
              alt="SpinHarvester" 
              className="h-10 w-auto"
              loading="eager"
              width="150"
              height="40"
            />
            <span className="text-2xl font-bold text-gray-900">SpinHarvester</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors ${
                isActive('/') ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              Home
            </Link>
            <Link
              to="/comparison"
              className={`font-medium transition-colors ${
                isActive('/comparison') ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              Compare Offers
            </Link>
            <Link
              to="/about"
              className={`font-medium transition-colors ${
                isActive('/about') ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              About Us
            </Link>
            <Link
              to="/faq"
              className={`font-medium transition-colors ${
                isActive('/faq') ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              FAQ
            </Link>
            <div className="relative" ref={searchRef}>
              {isSearchOpen ? (
                <div className="absolute right-0 top-0 bg-white rounded-lg shadow-2xl border border-gray-200 w-80 z-50">
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <FaSearch className="text-gray-400" />
                      <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search casinos, pages..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 outline-none text-gray-900 placeholder-gray-400"
                      />
                      <button
                        onClick={() => {
                          setIsSearchOpen(false);
                          setSearchQuery('');
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes />
                      </button>
                    </div>
                    {searchResults.length > 0 && (
                      <div className="max-h-64 overflow-y-auto">
                        {searchResults.map((result, index) => (
                          <button
                            key={index}
                            onClick={() => handleResultClick(result.url)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-3"
                          >
                            <FaSearch className="text-gray-400 text-sm" />
                            <div>
                              <div className="font-medium text-gray-900">{result.name}</div>
                              <div className="text-xs text-gray-500 capitalize">{result.type}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchQuery && searchResults.length === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No results found
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleSearchClick}
                  className="p-2 text-gray-700 hover:text-primary-600 transition-colors"
                  aria-label="Search"
                >
                  <FaSearch className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-700"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-4 space-y-3 slide-in-from-top">
            <Link
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className={`block py-2 font-medium ${
                isActive('/') ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              Home
            </Link>
            <Link
              to="/comparison"
              onClick={() => setIsMenuOpen(false)}
              className={`block py-2 font-medium ${
                isActive('/comparison') ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              Compare Offers
            </Link>
            <Link
              to="/about"
              onClick={() => setIsMenuOpen(false)}
              className={`block py-2 font-medium ${
                isActive('/about') ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              About Us
            </Link>
            <Link
              to="/faq"
              onClick={() => setIsMenuOpen(false)}
              className={`block py-2 font-medium ${
                isActive('/faq') ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              FAQ
            </Link>
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsSearchOpen(true);
              }}
              className="flex items-center gap-2 py-2 text-gray-700 hover:text-primary-600"
            >
              <FaSearch className="w-5 h-5" />
              Search
            </button>
          </div>
        )}

        {/* Mobile Search */}
        {isSearchOpen && (
          <div className="md:hidden border-t border-gray-200 pt-4 pb-4" ref={searchRef}>
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FaSearch className="text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search casinos, pages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 outline-none text-gray-900 placeholder-gray-400"
                  />
                  <button
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery('');
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <FaTimes />
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div className="max-h-64 overflow-y-auto">
                    {searchResults.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleResultClick(result.url)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-3"
                      >
                        <FaSearch className="text-gray-400 text-sm" />
                        <div>
                          <div className="font-medium text-gray-900">{result.name}</div>
                          <div className="text-xs text-gray-500 capitalize">{result.type}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchQuery && searchResults.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No results found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;

